import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ToastContainer } from "react-toastify";
import { enUS, ptBR, esES } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import logoLight from "./assets/logo.png";
import logoDark from "./assets/terachats_branco.png";
import Routes from "./routes";

const queryClient = new QueryClient();

const App = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                    backgroundColor: "#4568A6",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#F3F3F3" : "#171717",
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "#4568A6" : "#FFFFFF" },
                background: {
                    default: mode === "light" ? "#F3F3F3" : "#1D1D1D",
                    paper: mode === "light" ? "#F3F3F3" : "#1D1D1D",
                },
                textPrimary: mode === "light" ? "#4568A6" : "#FFFFFF",
                borderPrimary: mode === "light" ? "#4568A6" : "#FFFFFF",
                dark: { main: mode === "light" ? "#171717" : "#F3F3F3" },
                light: { main: mode === "light" ? "#F3F3F3" : "#171717" },
                tabHeaderBackground: mode === "light" ? "#EEE" : "#1D1D1D",
                optionsBackground: mode === "light" ? "#fafafa" : "#171717",
                options: mode === "light" ? "#fafafa" : "#1D1D1D",
                fontecor: mode === "light" ? "#128c7e" : "#fff",
                fancyBackground: mode === "light" ? "#fafafa" : "#171717",
                bordabox: mode === "light" ? "#eee" : "#171717",
                newmessagebox: mode === "light" ? "#eee" : "#171717",
                inputdigita: mode === "light" ? "#fff" : "#1D1D1D",
                contactdrawer: mode === "light" ? "#fff" : "#1D1D1D",
                announcements: mode === "light" ? "#ededed" : "#171717",
                login: mode === "light" ? "#fff" : "linear-gradient(to bottom, #171717, #1D1D1D)",
                loginbackground: mode === "light" ? "#fafdff" : "linear-gradient(to bottom, #161616ff, #171717)",
                textmode: mode === "light" ? "#1D1D1D" : "#fff",
                announcementspopover: mode === "light" ? "#fff" : "#1D1D1D",
                chatlist: mode === "light" ? "#eee" : "#1D1D1D",
                boxlist: mode === "light" ? "#ededed" : "#1D1D1D",
                boxchatlist: mode === "light" ? "#ededed" : "#171717",
                total: mode === "light" ? "#fff" : "#222",
                messageIcons: mode === "light" ? "grey" : "#F3F3F3",
                inputBackground: mode === "light" ? "#FFFFFF" : "#171717",
                barraSuperior: mode === "light" ? "linear-gradient(to right, #4568A6, #3a5e9bff , #335083ff)" : "linear-gradient(to right, #171717, #1D1D1D, #121212)",
                boxticket: mode === "light" ? "#EEE" : "#1D1D1D",
                campaigntab: mode === "light" ? "#ededed" : "#1D1D1D",
                mediainput: mode === "light" ? "#ededed" : "#1c1c1c",
                appbar: mode === "light" ? "#ededed" : "#1c1c1c",
            },
            mode,
            logo: mode == "light" ? logoLight : logoDark
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale = i18nlocale?.substring(0, 2) ?? 'pt';

        if (browserLocale === "pt") {
            setLocale(ptBR);
        } else if (browserLocale === "en") {
            setLocale(enUS)
        } else if (browserLocale === "es")
            setLocale(esES)

    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);

    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                    <SocketContext.Provider value={SocketManager}>
                        <ToastContainer
                            theme={theme.palette.type}
                            pauseOnHover
                        />
                        <Routes />
                    </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );

};

export default App;
